import { useMemo } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import Button from 'app/components/base/Button'
import Dialog from 'app/components/base/Dialog'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import TextFieldNumber from 'app/components/fields/TextFieldNumber'
import { getApproxPrice } from 'app/features/Profile/Auction/helpers/auctions.helpers'
import useCurrencyState from 'app/hooks/useCurrencyState'
import useFormErrorHandler from 'app/hooks/useFormErrorHandler'
import Alert from 'app/services/Alert'
import { isErrorHandlerType } from 'app/services/ErrorHandler'
import { usePlaceBidMutation } from 'app/services/Query/auction/auction.mutation'
import { transformFieldPrice } from 'app/utils/helpers/form.helpers'
import { PlaceBidSchema } from 'app/validation/schemas/listing/listing.schemas'

interface BidDialogProps {
  open: boolean
  onClose: () => void
  auctionId: number
  currency: string
  exchangeRate?: number
  onSuccess?: () => void
  onError?: () => void
}

const BidDialog: RFC<BidDialogProps> = ({
  open,
  onClose,
  auctionId,
  currency,
  exchangeRate = 1,
  onError,
  onSuccess
}) => {
  const intl = useIntl()
  const { currency: userCurrency } = useCurrencyState()

  const methods = useForm<PlaceBidFV>({
    defaultValues: {
      price: ''
    },
    resolver: yupResolver(PlaceBidSchema),
    mode: 'all'
  })
  const handleFormError = useFormErrorHandler(methods.setError)

  const { mutateAsync } = usePlaceBidMutation()

  const currentPrice = useWatch({ control: methods.control, name: 'price' })

  const showApproxPrice = exchangeRate !== 1

  const convertedApproxPrice = useMemo(() => {
    if (!!currentPrice && showApproxPrice) {
      return getApproxPrice({ price: currentPrice, exchangeRate })
    }

    return 0
  }, [currentPrice, showApproxPrice])

  const approxPrice = intl.formatNumber(convertedApproxPrice, {
    currency: userCurrency,
    style: 'currency'
  })

  const { isSubmitting, isDirty } = methods.formState

  const onSubmit = async (values: PlaceBidFV) => {
    try {
      const { price } = values

      await mutateAsync({ auctionId, price })

      Alert.success(intl.formatMessage({ defaultMessage: 'Success' }))

      onClose()

      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      if (isErrorHandlerType(error)) {
        if (
          !!error?.data?.errors &&
          '_flaw' in error?.data?.errors &&
          error?.data?.errors?._flaw
        ) {
          Alert.bidAttention()
          return
        }

        handleFormError(error?.data)

        if (onError) {
          onError()
        }
      }
    }
  }

  return (
    <Dialog
      open={open}
      disableScroll
      titleSx={{ alignSelf: 'center', mb: 3 }}
      title={<FormattedMessage defaultMessage="Place a Bid" />}
      onClose={onClose}
      actions={
        <Button
          size="small"
          onClick={methods.handleSubmit(onSubmit)}
          loading={isSubmitting}
          disabled={!isDirty}
          sx={{ width: '100%' }}
        >
          <FormattedMessage defaultMessage="Submit" />
        </Button>
      }
      contentSx={{ mb: 2 }}
      actionsSx={{ paddingTop: 0 }}
    >
      <FormProvider {...methods}>
        <Stack>
          <Stack spacing={1}>
            <TextFieldNumber
              name="price"
              variant="currency"
              placeholder="0.00"
              currency={currency}
              decimalScale={2}
              transform={transformFieldPrice}
              {...(showApproxPrice && {
                helperText: (
                  <FormattedMessage
                    defaultMessage="Approx. {approxPrice}"
                    values={{ approxPrice }}
                  />
                )
              })}
            />

            <Text
              variant="body2"
              sx={{ textAlign: 'center' }}
              color="text.third"
            >
              <FormattedMessage defaultMessage="By clicking Submit you are agreeing to buy this item if your bid is accepted" />
            </Text>
          </Stack>
        </Stack>
      </FormProvider>
    </Dialog>
  )
}

export default BidDialog
